import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Building2, ArrowRight, ArrowLeft, Check, Loader2, Shield, FileText, Eye, EyeOff } from 'lucide-react';
import PasswordStrengthMeter from '../../components/PasswordStrengthMeter';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CorporateRegistration = () => {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [success, setSuccess] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [refData, setRefData] = useState({ industries: [], company_sizes: [], departments: [] });
  const [form, setForm] = useState({
    org_name: '', industry: '', org_type: '', website: '',
    address_line1: '', city: '', state: '', zip_code: '', country: '',
    team_names: [], member_count_estimate: '',
    contact_name: '', contact_title: '', admin_email: '', admin_password: '', phone: '',
    has_data_processing_agreement: false, has_read_terms_of_service: false, referral_source: '',
  });

  useEffect(() => {
    fetch(`${API_URL}/api/corporate/reference-data`)
      .then(r => r.json()).then(setRefData).catch(() => {});
  }, []);

  const update = (field, value) => setForm(p => ({ ...p, [field]: value }));
  const toggleDept = (d) => setForm(p => ({
    ...p,
    team_names: p.team_names.includes(d)
      ? p.team_names.filter(x => x !== d)
      : [...p.team_names, d],
  }));

  const step1Valid = form.org_name && form.industry && form.org_type;
  const step2Valid = form.contact_name && form.contact_title && form.admin_email && form.admin_password.length >= 8 && form.has_data_processing_agreement && form.has_read_terms_of_service;

  const submit = async () => {
    setLoading(true);
    setError('');
    try {
      const body = { ...form, member_count_estimate: parseInt(form.member_count_estimate) || 0 };
      const res = await fetch(`${API_URL}/api/corporate/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Registration failed');
      setResult(data);
      setSuccess(true);
    } catch (err) {
      setError(err.message);
    }
    setLoading(false);
  };

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4" style={{ backgroundColor: '#F8F7F4' }}>
        <div className="max-w-lg w-full text-center" data-testid="registration-success">
          <div className="bg-white rounded-2xl p-8" style={{ border: '1px solid #E8E8E8' }}>
            <div className="w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center" style={{ backgroundColor: '#2EAA5C15' }}>
              <Check size={32} style={{ color: '#2EAA5C' }} />
            </div>
            <h1 className="text-xl font-bold mb-2" style={{ color: '#1A1209' }}>Your 30-Day Free Trial Is Active</h1>
            <p className="text-sm mb-6" style={{ color: '#555' }}>
              Welcome to HiBFF for Business! A confirmation email with your access code and next steps has been sent to <strong>{form.admin_email}</strong>.
            </p>
            <div className="rounded-xl p-4 mb-6 text-left" style={{ backgroundColor: '#F8F7F4' }}>
              <h3 className="text-xs font-semibold uppercase tracking-wider mb-3" style={{ color: '#777' }}>Registration Summary</h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between"><span style={{ color: '#777' }}>Organisation</span><span className="font-medium" style={{ color: '#1A1209' }}>{form.org_name}</span></div>
                <div className="flex justify-between"><span style={{ color: '#777' }}>Industry</span><span style={{ color: '#333' }}>{form.industry}</span></div>
                <div className="flex justify-between"><span style={{ color: '#777' }}>Size</span><span style={{ color: '#333' }}>{form.org_type} employees</span></div>
                <div className="flex justify-between"><span style={{ color: '#777' }}>Contact</span><span style={{ color: '#333' }}>{form.contact_name}</span></div>
                {result?.access_code && (
                  <div className="flex justify-between"><span style={{ color: '#777' }}>Team access code</span><span className="font-bold tracking-widest" style={{ color: '#F5831F' }} data-testid="access-code">{result.access_code}</span></div>
                )}
              </div>
            </div>
            <Link to="/login" className="inline-flex items-center gap-2 px-6 py-3 rounded-xl text-sm font-semibold text-white" style={{ backgroundColor: '#F5831F' }} data-testid="back-to-login">
              Back to Login <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ backgroundColor: '#F8F7F4' }}>
      <div className="w-full" style={{ maxWidth: 560 }}>
        {/* Header */}
        <div className="text-center mb-6">
          <div className="flex items-center justify-center gap-2 mb-2">
            <Building2 size={24} style={{ color: '#F5831F' }} />
            <span className="text-lg font-bold" style={{ color: '#1A1209' }}>HiBFF for Business</span>
          </div>
          <p className="text-sm font-medium" style={{ color: '#555' }}>Register your organisation — anywhere in the world — for a 30-day free trial</p>
        </div>

        <div className="bg-white rounded-2xl overflow-hidden" style={{ border: '1px solid #E8E8E8' }}>
          {/* Progress */}
          <div className="flex items-center gap-0 px-8 pt-6 pb-4">
            {[1, 2].map(s => (
              <React.Fragment key={s}>
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold"
                    style={s === step ? { backgroundColor: '#F5831F', color: 'white' } : s < step ? { backgroundColor: '#2EAA5C', color: 'white' } : { backgroundColor: '#F0F0F0', color: '#666' }}
                    data-testid={`step-indicator-${s}`}>
                    {s < step ? <Check size={14} /> : s}
                  </div>
                  <span className="text-xs font-medium" style={{ color: s === step ? '#1A1209' : '#999' }}>
                    {s === 1 ? 'Company Details' : 'Contact & Account'}
                  </span>
                </div>
                {s < 2 && <div className="flex-1 h-px mx-3" style={{ backgroundColor: s < step ? '#2EAA5C' : '#E8E8E8' }} />}
              </React.Fragment>
            ))}
          </div>

          <div className="px-8 pb-8">
            {error && (
              <div className="mb-4 p-3 rounded-lg text-sm" style={{ backgroundColor: '#FEF2F2', color: '#DC2626', border: '1px solid #FECACA' }} data-testid="error-message">
                {error}
              </div>
            )}

            {/* Step 1: Company Details */}
            {step === 1 && (
              <div data-testid="step-1">
                <div className="space-y-4">
                  <div>
                    <label className="block text-xs font-semibold mb-1" style={{ color: '#1A1209' }}>Company Name *</label>
                    <input value={form.org_name} onChange={e => update('org_name', e.target.value)}
                      className="w-full px-4 py-2.5 border rounded-xl text-sm" style={{ borderColor: '#D0D0D0' }}
                      placeholder="Acme Corp" data-testid="company-name" />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-semibold mb-1" style={{ color: '#1A1209' }}>Industry *</label>
                      <select value={form.industry} onChange={e => update('industry', e.target.value)}
                        className="w-full px-4 py-2.5 border rounded-xl text-sm" style={{ borderColor: '#D0D0D0' }} data-testid="industry">
                        <option value="">Select industry</option>
                        {refData.industries.map(i => <option key={i} value={i}>{i}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-semibold mb-1" style={{ color: '#1A1209' }}>Company Size *</label>
                      <select value={form.org_type} onChange={e => update('org_type', e.target.value)}
                        className="w-full px-4 py-2.5 border rounded-xl text-sm" style={{ borderColor: '#D0D0D0' }} data-testid="company-size">
                        <option value="">Select size</option>
                        {refData.company_sizes.map(s => <option key={s} value={s}>{s}</option>)}
                      </select>
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold mb-1" style={{ color: '#1A1209' }}>Website</label>
                    <input value={form.website} onChange={e => update('website', e.target.value)}
                      className="w-full px-4 py-2.5 border rounded-xl text-sm" style={{ borderColor: '#D0D0D0' }}
                      placeholder="https://acmecorp.com" data-testid="website" />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold mb-1" style={{ color: '#1A1209' }}>Departments</label>
                    <p className="text-xs mb-2" style={{ color: '#666' }}>Select the departments that will participate</p>
                    <div className="flex flex-wrap gap-2" data-testid="departments">
                      {refData.departments.map(d => (
                        <button key={d} onClick={() => toggleDept(d)}
                          className="px-3 py-1.5 rounded-full text-xs font-medium transition-all"
                          style={form.team_names.includes(d)
                            ? { backgroundColor: '#F5831F', color: 'white' }
                            : { backgroundColor: '#F0F0F0', color: '#666' }}
                          data-testid={`dept-${d.toLowerCase().replace(/\s+/g, '-')}`}>
                          {d}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-semibold mb-1" style={{ color: '#1A1209' }}>Address</label>
                      <input value={form.address_line1} onChange={e => update('address_line1', e.target.value)}
                        className="w-full px-4 py-2.5 border rounded-xl text-sm" style={{ borderColor: '#D0D0D0' }}
                        placeholder="1 High Street" data-testid="address" />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold mb-1" style={{ color: '#1A1209' }}>City</label>
                      <input value={form.city} onChange={e => update('city', e.target.value)}
                        className="w-full px-4 py-2.5 border rounded-xl text-sm" style={{ borderColor: '#D0D0D0' }}
                        placeholder="London / New York" data-testid="city" />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-semibold mb-1" style={{ color: '#1A1209' }}>State / Province / County</label>
                      <input value={form.state} onChange={e => update('state', e.target.value)}
                        className="w-full px-4 py-2.5 border rounded-xl text-sm" style={{ borderColor: '#D0D0D0' }}
                        placeholder="Greater London / California" data-testid="state" />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold mb-1" style={{ color: '#1A1209' }}>Postcode / Zip Code</label>
                      <input value={form.zip_code} onChange={e => update('zip_code', e.target.value)}
                        className="w-full px-4 py-2.5 border rounded-xl text-sm" style={{ borderColor: '#D0D0D0' }}
                        placeholder="SW1A 1AA / 94105" data-testid="zip-code" />
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold mb-1" style={{ color: '#1A1209' }}>Country</label>
                    <input value={form.country} onChange={e => update('country', e.target.value)}
                      className="w-full px-4 py-2.5 border rounded-xl text-sm" style={{ borderColor: '#D0D0D0' }}
                      placeholder="United Kingdom / United States / Singapore" data-testid="country" />
                  </div>
                </div>
                <button onClick={() => setStep(2)} disabled={!step1Valid}
                  className="w-full mt-6 py-3 rounded-xl text-sm font-semibold text-white flex items-center justify-center gap-2 disabled:opacity-40"
                  style={{ backgroundColor: '#F5831F' }} data-testid="next-step-btn">
                  Continue <ArrowRight size={16} />
                </button>
              </div>
            )}

            {/* Step 2: Contact & Account */}
            {step === 2 && (
              <div data-testid="step-2">
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-semibold mb-1" style={{ color: '#1A1209' }}>Your Full Name *</label>
                      <input value={form.contact_name} onChange={e => update('contact_name', e.target.value)}
                        className="w-full px-4 py-2.5 border rounded-xl text-sm" style={{ borderColor: '#D0D0D0' }}
                        placeholder="Jane Smith" data-testid="contact-name" />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold mb-1" style={{ color: '#1A1209' }}>Job Title *</label>
                      <input value={form.contact_title} onChange={e => update('contact_title', e.target.value)}
                        className="w-full px-4 py-2.5 border rounded-xl text-sm" style={{ borderColor: '#D0D0D0' }}
                        placeholder="Head of People" data-testid="contact-title" />
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold mb-1" style={{ color: '#1A1209' }}>Work Email *</label>
                    <input type="email" value={form.admin_email} onChange={e => update('admin_email', e.target.value)}
                      className="w-full px-4 py-2.5 border rounded-xl text-sm" style={{ borderColor: '#D0D0D0' }}
                      placeholder="jane@acmecorp.com" data-testid="admin-email" />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold mb-1" style={{ color: '#1A1209' }}>Create Password *</label>
                    <div className="relative">
                      <input type={showPassword ? 'text' : 'password'} value={form.admin_password} onChange={e => update('admin_password', e.target.value)}
                        className="w-full px-4 py-2.5 pr-10 border rounded-xl text-sm" style={{ borderColor: '#D0D0D0' }}
                        placeholder="Minimum 8 characters" data-testid="admin-password" />
                      <button type="button" onClick={() => setShowPassword(s => !s)} aria-label={showPassword ? 'Hide password' : 'Show password'} data-testid="admin-password-toggle-btn" tabIndex={-1} className="absolute right-3 top-1/2 -translate-y-1/2 p-0.5 text-gray-400 hover:text-gray-600">
                        {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                      </button>
                    </div>
                    {form.admin_password && form.admin_password.length < 8 && (
                      <p className="text-xs mt-1" style={{ color: '#DC2626' }}>Password must be at least 8 characters</p>
                    )}
                    <PasswordStrengthMeter password={form.admin_password} testIdPrefix="corp-pwd-strength" />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold mb-1" style={{ color: '#1A1209' }}>Phone Number</label>
                    <input value={form.phone} onChange={e => update('phone', e.target.value)}
                      className="w-full px-4 py-2.5 border rounded-xl text-sm" style={{ borderColor: '#D0D0D0' }}
                      placeholder="+1 (555) 123-4567" data-testid="phone" />
                  </div>

                  {/* Compliance checkboxes */}
                  <div className="rounded-xl p-4" style={{ backgroundColor: '#F8F7F4' }}>
                    <div className="flex items-start gap-3 mb-3">
                      <input type="checkbox" checked={form.has_data_processing_agreement} onChange={e => update('has_data_processing_agreement', e.target.checked)}
                        className="mt-1 rounded" data-testid="privacy-checkbox" />
                      <label className="text-xs" style={{ color: '#666' }}>
                        <Shield size={12} className="inline mr-1" style={{ color: '#F5831F' }} />
                        I confirm I have read and agree to the <a href="/privacy" target="_blank" className="underline font-semibold" style={{ color: '#F5831F' }}>HiBFF Privacy Policy</a> *
                      </label>
                    </div>
                    <div className="flex items-start gap-3">
                      <input type="checkbox" checked={form.has_read_terms_of_service} onChange={e => update('has_read_terms_of_service', e.target.checked)}
                        className="mt-1 rounded" data-testid="dpa-checkbox" />
                      <label className="text-xs" style={{ color: '#666' }}>
                        <FileText size={12} className="inline mr-1" style={{ color: '#F5831F' }} />
                        I confirm I have read and agree to the <span className="underline font-semibold" style={{ color: '#F5831F' }}>Data Processing Agreement</span> *
                      </label>
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-semibold mb-1" style={{ color: '#1A1209' }}>How did you hear about us?</label>
                    <select value={form.referral_source} onChange={e => update('referral_source', e.target.value)}
                      className="w-full px-4 py-2.5 border rounded-xl text-sm" style={{ borderColor: '#D0D0D0' }} data-testid="referral-source">
                      <option value="">Select (optional)</option>
                      <option value="linkedin">LinkedIn</option>
                      <option value="conference">Conference / Event</option>
                      <option value="referral">Employee Referral</option>
                      <option value="search">Google Search</option>
                      <option value="press">Press / Media</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                </div>

                <div className="flex gap-3 mt-6">
                  <button onClick={() => setStep(1)}
                    className="flex-1 py-3 rounded-xl text-sm font-semibold flex items-center justify-center gap-2"
                    style={{ border: '1px solid #E0E0E0', color: '#666' }} data-testid="back-btn">
                    <ArrowLeft size={16} /> Back
                  </button>
                  <button onClick={submit} disabled={!step2Valid || loading}
                    className="flex-1 py-3 rounded-xl text-sm font-semibold text-white flex items-center justify-center gap-2 disabled:opacity-40"
                    style={{ backgroundColor: '#F5831F' }} data-testid="submit-btn">
                    {loading ? <><Loader2 size={16} className="animate-spin" /> Submitting...</> : <>Submit Application <ArrowRight size={16} /></>}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        <p className="text-center text-xs mt-4" style={{ color: '#777' }}>
          Already registered? <Link to="/login" className="underline font-semibold" style={{ color: '#F5831F' }}>Log in here</Link>
        </p>
      </div>
    </div>
  );
};

export default CorporateRegistration;
